import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategoriesWithSubcategories } from '../../../hooks/useCategoriesWithSubcategories';
import {
  getFreeCoursesPath,
  getSubcategoryCoursesPath,
  hasSubcategories,
  isFreeCoursesCategory,
} from '../../../utils/categoryNav';

function CoursesNavIcon({ className = 'mr-1 h-5 w-5 shrink-0' }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1.66699 1.66663H9.16699V9.16663H1.66699V1.66663ZM14.5837 1.66663C16.667 1.66663 18.3337 3.33329 18.3337 5.41663C18.3337 7.49996 16.667 9.16663 14.5837 9.16663C12.5003 9.16663 10.8337 7.49996 10.8337 5.41663C10.8337 3.33329 12.5003 1.66663 14.5837 1.66663ZM5.41699 11.6666L9.16699 18.3333H1.66699L5.41699 11.6666ZM15.8337 14.1666H18.3337V15.8333H15.8337V18.3333H14.167V15.8333H11.667V14.1666H14.167V11.6666H15.8337V14.1666Z"
        fill="currentColor"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg className="h-4 w-4 shrink-0 text-[#666666]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function CoursesCategoryDropdown() {
  const [open, setOpen] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { categories, loading, error } = useCategoriesWithSubcategories();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setActiveCategoryId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMenu = () => {
    setOpen(false);
    setActiveCategoryId(null);
  };

  const handleCategoryClick = (category) => {
    if (isFreeCoursesCategory(category)) {
      navigate(getFreeCoursesPath());
      closeMenu();
      return;
    }

    if (hasSubcategories(category)) {
      setActiveCategoryId((current) => (current === category.id ? null : category.id));
      return;
    }

    closeMenu();
  };

  const handleSubcategoryClick = (category, subcategory) => {
    navigate(getSubcategoryCoursesPath(category.id, subcategory.id), {
      state: {
        categoryName: category.name,
        subcategoryName: subcategory.name,
      },
    });
    closeMenu();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`flex items-center rounded-xl text-base font-medium transition-colors xl:text-[20px] ${
          open ? 'bg-white text-[#9369CC] shadow-sm' : 'text-[#1B1B1B] hover:text-[#9369CC]'
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <CoursesNavIcon />
        Courses
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-3 min-w-[220px] rounded-2xl border border-gray-100 bg-white py-2 shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
          {loading ? (
            <p className="px-5 py-3 text-sm text-[#666666]">Loading categories...</p>
          ) : error ? (
            <p className="px-5 py-3 text-sm text-red-600">{error}</p>
          ) : categories.length ? (
            categories.map((category) => {
              const isActive = activeCategoryId === category.id;
              const showSubcategories = hasSubcategories(category);

              return (
                <div key={category.id} className="relative">
                  <button
                    type="button"
                    onMouseEnter={() => {
                      setActiveCategoryId(showSubcategories ? category.id : null);
                    }}
                    onClick={() => handleCategoryClick(category)}
                    className={`flex w-full items-center justify-between px-5 py-3 text-left text-base font-medium transition-colors ${
                      isActive ? 'bg-[#EEF4FF] text-[#9369CC]' : 'text-[#1B1B1B] hover:bg-[#EEF4FF] hover:text-[#9369CC]'
                    }`}
                  >
                    <span>{category.name}</span>
                    {showSubcategories && <ChevronRight />}
                  </button>

                  {showSubcategories && isActive && (
                    <div className="absolute left-full top-0 z-50 ml-2 min-w-[200px] rounded-2xl border border-gray-100 bg-white py-2 shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                      {category.subcategories.map((subcategory) => (
                        <button
                          key={subcategory.id}
                          type="button"
                          onClick={() => handleSubcategoryClick(category, subcategory)}
                          className="block w-full px-5 py-3 text-left text-base font-medium text-[#1B1B1B] transition-colors hover:bg-[#EEF4FF] hover:text-[#9369CC]"
                        >
                          {subcategory.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <p className="px-5 py-3 text-sm text-[#666666]">No categories available.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CoursesCategoryDropdown;
